/**
 * ANJA policy pages: moves Shopify's policy text into a two-column reading layout with
 * a contents list (from the policy's headings), scroll-spy, search with highlights and reading progress.
 * The policy text is not changed.
 */
const shell = /** @type {HTMLElement | null} */ (document.querySelector('[data-anja-policy-shell]'));
const container = /** @type {HTMLElement | null} */ (document.querySelector('.shopify-policy__container, .anja-policy-page'));

if (shell && container) {
  const body = /** @type {HTMLElement | null} */ (container.querySelector('.shopify-policy__body, .rte, .anja-policy-page__body')) || container;
  const titleEl = container.querySelector('.shopify-policy__title h1, h1');
  const title = titleEl?.textContent?.trim() || document.title.split('–')[0].trim();
  /** @type {HTMLElement} */ (shell.querySelector('[data-anja-policy-title]')).textContent = title;
  titleEl?.closest('.shopify-policy__title')?.setAttribute('hidden', '');
  if (titleEl && !titleEl.closest('.shopify-policy__title')) titleEl.setAttribute('hidden', '');

  // Active tab
  shell.querySelectorAll('.anja-policy-tabs a').forEach((a) => {
    if (new URL(/** @type {HTMLAnchorElement} */ (a).href).pathname === location.pathname.replace(/^\/[a-z]{2}(?=\/)/, '')) a.setAttribute('aria-current', 'page');
    else if (location.pathname.endsWith(new URL(/** @type {HTMLAnchorElement} */ (a).href).pathname)) a.setAttribute('aria-current', 'page');
  });

  // Layout: [aside | article]
  const layout = document.createElement('div');
  layout.className = 'anja-policy-layout';
  const tpl = /** @type {HTMLTemplateElement} */ (shell.querySelector('[data-anja-policy-aside]'));
  const aside = /** @type {HTMLElement} */ (tpl.content.firstElementChild?.cloneNode(true));
  const article = document.createElement('article');
  article.className = 'anja-policy-article';
  const wrap = document.createElement('div');
  wrap.className = 'anja-policy-wrap';
  container.parentNode?.insertBefore(wrap, container);
  wrap.append(shell, layout);
  layout.append(aside, article);
  article.append(body);
  container.hidden = true;
  shell.hidden = false;

  // Headings: real headings, or paragraphs that are entirely bold
  let heads = /** @type {HTMLElement[]} */ (Array.from(body.querySelectorAll('h1, h2, h3, h4'))).filter((h) => (h.textContent || '').trim().length > 1);
  if (heads.length < 3) {
    heads = /** @type {HTMLElement[]} */ (
      Array.from(body.querySelectorAll('p, div')).filter((p) => {
        const strong = p.querySelector(':scope > strong, :scope > b');
        const text = p.textContent?.trim() || '';
        return strong && text.length < 90 && strong.textContent?.trim() === text;
      })
    );
    heads.forEach((h) => h.classList.add('anja-policy-heading'));
  }
  const toc = /** @type {HTMLElement} */ (aside.querySelector('[data-anja-policy-toc]'));
  /** @type {Map<Element, HTMLAnchorElement>} */
  const links = new Map();
  heads.forEach((h, i) => {
    if (!h.id) h.id = `policy-${i + 1}`;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${h.id}`;
    a.textContent = (h.textContent || '').trim().replace(/[:：]$/, '');
    a.addEventListener('click', (e) => {
      e.preventDefault();
      h.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (matchMedia('(max-width: 989px)').matches) /** @type {HTMLDetailsElement} */ (aside.querySelector('details')).open = false;
    });
    li.append(a);
    toc.append(li);
    links.set(h, a);
  });
  if (!heads.length) aside.querySelector('.anja-policy-toc')?.setAttribute('hidden', '');
  if (matchMedia('(max-width: 989px)').matches) /** @type {HTMLDetailsElement} */ (aside.querySelector('details')).open = false;

  const spy = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((l) => l.removeAttribute('aria-current'));
        links.get(e.target)?.setAttribute('aria-current', 'true');
      });
    },
    { rootMargin: '-20% 0px -70% 0px' }
  );
  heads.forEach((h) => spy.observe(h));

  // Reading progress
  const bar = /** @type {HTMLElement | null} */ (shell.querySelector('[data-anja-policy-progress]'));
  const progress = () => {
    const r = article.getBoundingClientRect();
    const total = r.height - innerHeight;
    if (bar) bar.style.transform = `scaleX(${total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 1})`;
  };
  document.addEventListener('scroll', progress, { passive: true, capture: true });
  progress();

  // Search with highlights
  const input = /** @type {HTMLInputElement} */ (shell.querySelector('[data-anja-policy-search]'));
  const count = /** @type {HTMLElement} */ (shell.querySelector('[data-anja-policy-count]'));
  let marks = /** @type {HTMLElement[]} */ ([]);
  let current = -1;
  const clear = () => {
    marks.forEach((m) => m.replaceWith(document.createTextNode(m.textContent || '')));
    body.normalize();
    marks = [];
    current = -1;
  };
  const go = (i) => {
    if (!marks.length) return;
    marks[current]?.classList.remove('is-current');
    current = (i + marks.length) % marks.length;
    marks[current].classList.add('is-current');
    marks[current].scrollIntoView({ behavior: 'smooth', block: 'center' });
    count.textContent = `${current + 1} / ${marks.length}`;
  };
  let timer = 0;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      clear();
      const q = input.value.trim().toLowerCase();
      if (q.length < 2) {
        count.textContent = '';
        return;
      }
      const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(/** @type {Text} */ (walker.currentNode));
      nodes.forEach((node) => {
        const text = node.nodeValue || '';
        const lower = text.toLowerCase();
        let at = lower.indexOf(q);
        if (at === -1) return;
        const frag = document.createDocumentFragment();
        let last = 0;
        while (at !== -1) {
          frag.append(text.slice(last, at));
          const mark = document.createElement('mark');
          mark.className = 'anja-policy-mark';
          mark.textContent = text.slice(at, at + q.length);
          frag.append(mark);
          marks.push(mark);
          last = at + q.length;
          at = lower.indexOf(q, last);
        }
        frag.append(text.slice(last));
        node.replaceWith(frag);
      });
      count.textContent = marks.length ? `${marks.length}` : '0';
      if (marks.length) go(0);
    }, 200);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      go(current + (e.shiftKey ? -1 : 1));
    }
  });
}
